import * as _ from "lodash";
import * as React from "react";
import styled, { css } from "styled-components";
import { useMappedState } from "redux-react-hook";

import Grid from "src/library/components/Hedron";
import { Icon } from "src/library/components/Icon";
import { Text } from "src/library/components/Text";
import { Tooltip } from "src/library/components/Tooltip";
import { Icons } from "src/library/components/Icon/types";
import {
  InstalledModuleType,
  ERPUnity
} from "src/utils/constants";
import { IProduct } from "src/core/products/api/products.api";
import { IOrderLine } from "src/core/orders/api/orders.api";
import { IListLine } from "src/core/lists/api/lists.api";
import { IPrice } from "src/core/shared/api/price-change.api";
import {
  appliedDiscountTypePrice,
  formatPrice,
  totalPrice,
  unitPrice
} from "src/core/prices/utils/price-helpers";
import { useProductLatestPackSize } from "src/core/products/hooks/useProductLatestPackSize";
import { IStore } from "src/core/shared/store";
import { useDrop } from "src/core/drops/hooks";

import {
  unitPriceVATOnly,
  totalPriceVATOnly,
  percentageToPrice,
  totalPriceWithVAT,
  unitPriceWithVAT
} from "../utils/price-helpers";

interface PriceProps {
  product: IProduct | null;
  quantity?: number;
  installedModules: InstalledModuleType[];
  currency?: string;
  type: "unit" | "total";
  line?: IOrderLine | IListLine;
  // view and credit will show unit pricing. Only show packsize pricing in edit mode
  mode?: "edit" | "view" | "credit";
  usePackSizeMultiplier?: boolean;
  hide?: boolean;
}

/**
 * Component for displaying a product price
 *
 * @param {*} { product, quantity, installedModules, currency, type, mode, line }
 * @returns
 */
const Price: React.ReactFCWithChildren<PriceProps> = ({
  product, quantity, installedModules, currency, type, line, usePackSizeMultiplier = true, mode = "view", hide = false
}) => {
  const [ groupedPrices, setGroupedPrices ] = React.useState<{ [key: string]: IPrice[] }>();
  const [ basePrice, setBasePrice ] = React.useState<IPrice | null>(null);
  const [ currentBand, setCurrentBand ] = React.useState<IPrice | null>(null);
  const [ nextBand, setNextBand ] = React.useState<IPrice | null>(null);
  const [ { packSize } ] = useProductLatestPackSize(product);
  const [ { drop } ] = useDrop();

  const { hasPackSizes, erp } = useMappedState(({ global }: IStore) => ({
    hasPackSizes: global.settings?.hasPackSizes,
    erp: global.settings?.erp?.name
  }));

  // on mount setup any price bands etcs
  React.useEffect(() => {
    if (product) {
      if (!groupedPrices) {
        if (!installedModules.includes("price-tier")) {
          if (product.prices) {
            setGroupedPrices({ 0: product.prices });
          }
        } else {
          setGroupedPrices(_.groupBy(product.prices, "minQuantity"));
        }
      }

      if (!basePrice) {
        if (product.prices && product.prices[ 0 ]) {
          setBasePrice(product.prices[ 0 ]);
        }
      }
    }
  }, [ product, installedModules ]);

  // handle quantity changes
  React.useEffect(() => {
    setCurrentPriceBand();
  }, [ groupedPrices ]);

  React.useEffect(() => {
    if (type === "total") {
      setCurrentPriceBand();
    }
  }, [ quantity ]);

  // handle the setting of the next closest band
  React.useEffect(() => {
    setNextPriceBand();
  }, [ currentBand ]);

  // Function for Setting the current price band
  const setCurrentPriceBand = React.useCallback(() => {
    let selectedPrice = null;

    if (groupedPrices) {
      const bands = _.keys(groupedPrices);
      const quant = quantity || 1;

      for (const band of bands) {
        if (groupedPrices[ band ][ 0 ]) {
          const minQuantity = typeof groupedPrices[ band ][ 0 ].minQuantity === "number" ? groupedPrices[ band ][ 0 ].minQuantity : 0;

          if (quant >= (minQuantity as number)) {
            selectedPrice = groupedPrices[ band ][ 0 ];
          }
        }
      }
    }

    setCurrentBand(selectedPrice);
  }, [ quantity, groupedPrices ]);

  // Get the current price using the prices data on the product
  const getPriceFromBand = React.useCallback(
    (selectedPrice: IPrice | null) => {
      if (selectedPrice) {
        // if total
        if (type === "total") {
          const totalPrice = selectedPrice.price * (quantity || 1);

          return `${formatPrice(totalPrice, 2, currency)}`;
        }

        return `${formatPrice(selectedPrice.price, 3, currency)}`;
      }

      return "";
    },
    [
      type,
      currency,
      quantity
    ]
  );

  // get the price from the orderline data
  const priceFromline = React.useMemo(() => {
    if (line) {
      return {
        price: type === "total" ? totalPrice(line) : unitPrice(line),
        vat: type === "total" ? totalPriceVATOnly(line) : unitPriceVATOnly(line),
        priceWithVat: type === "total" ? totalPriceWithVAT(line) : unitPriceWithVAT(line)
      };
    }

    return null;
  }, [ line, type ]);

  const priceFromCurrentBand = React.useMemo(() => {
    if (currentBand) {
      const totalPrice = parseFloat((currentBand.price * (quantity || 1)).toFixed(2));
      const unitPrice = currentBand.price;
      const price = type === "total" ? totalPrice : unitPrice;
      const vat = (type === "total" ? percentageToPrice(totalPrice, currentBand.vatPercentage || 0) : percentageToPrice(unitPrice, currentBand.vatPercentage || 0)) || 0;
      const priceAfterDiscount = appliedDiscountTypePrice(price, drop?.discount);
      const vatAfterDiscount = appliedDiscountTypePrice(vat, drop?.discount);

      return {
        price: priceAfterDiscount,
        vat: vatAfterDiscount,
        priceWithVat: priceAfterDiscount + vatAfterDiscount
      };
    }

    return null;
  }, [
    currentBand,
    drop?.discount,
    quantity,
    type
  ]);

  const currentPrice = React.useMemo(() => {
    if ((mode === "view" || mode === "credit") && priceFromline) {
      return priceFromline;
    }

    return priceFromCurrentBand;
  }, [
    priceFromCurrentBand,
    priceFromline,
    mode
  ]);

  const packSizePrice = React.useMemo(() => {
    if (currentPrice && packSize) {
      return {
        price: currentPrice?.price * packSize?.quantity,
        vat: currentPrice?.vat * packSize?.quantity,
        priceWithVat: currentPrice?.priceWithVat * packSize?.quantity
      };
    }

    return;
  }, [ currentPrice, packSize ]);

  // set the next closest band
  const setNextPriceBand = React.useCallback(() => {
    if (currentBand) {
      const nextBand = _.find(groupedPrices, price => {
        return (price[ 0 ].minQuantity || 0) > (currentBand.minQuantity || 0);
      });

      if (nextBand && nextBand[ 0 ]) {
        setNextBand(nextBand[ 0 ]);

        return;
      }
    }

    setNextBand(null);
  }, [ currentBand, groupedPrices ]);

  // decides if we are close the the threshold of the next band
  const isCloseToBand = React.useCallback(() => {
    if (nextBand && nextBand.minQuantity && quantity) {
      const percentage = (quantity * 100) / nextBand.minQuantity;

      if (percentage >= 95) {
        return true;
      }
    }

    return false;
  }, [ nextBand, quantity ]);

  const priceBands = _.keys(groupedPrices);
  const tippyId = `${type}-${basePrice?.id}-tooltip`;

  // Helper function to format FOC prices with indicator
  const formatPriceWithFOC = React.useCallback((price: number, fractionDigits: 2 | 3 = 2) => {
    const formattedPrice = formatPrice(price, fractionDigits);

    // Only show FOC indicator for Unity ERP - other ERPs may have zero-priced related products
    return price === 0 && erp === ERPUnity ? `${formattedPrice} (FOC)` : formattedPrice;
  }, [ erp ]);

  if (hide) {
    return <></>;
  }

  return (
    <PriceWrapper direction="vertical">
      {currentBand !== basePrice && type === "unit" && (
        <div>
          <Text
            color="accent"
            className="old-price"
            fontSize="h4"
          >
            {`${getPriceFromBand(basePrice)}`}
          </Text>
        </div>
      )}

      <div className="current-price-wrapper ">
        <Text
          className="current-price"
          fontSize="h3"
        >
          {![ "view", "credit" ].includes(mode) && usePackSizeMultiplier && packSize && packSizePrice && type === "unit" ?
            formatPriceWithFOC(packSizePrice.priceWithVat, 2) :
            currentPrice && formatPriceWithFOC(currentPrice.priceWithVat, 2)}
        </Text>

        {mode !== "credit" && hasPackSizes && !!product?.packSizes?.length && currentPrice && type === "unit" && (
          <Text
            fontSize="sub"
            textTransform="none"
          >
            {!packSize || !usePackSizeMultiplier ? `per ${product?.unitCode || "unit"}` : `${formatPriceWithFOC(currentPrice.priceWithVat, 2)} per ${product?.unitCode || "unit"}`}
          </Text>
        )}

        {packSize && !!packSizePrice?.vat || !!currentPrice?.vat && (
          <>
            <div
              className="vat-wrapper"
              data-tip
              data-for={tippyId}
            >
              <Text className="inc-vat">
                {" inc. VAT"}
              </Text>
            </div>

            <Tooltip
              id={tippyId}
              type="dark"
              place="top"
              getContent={() => {
                return (
                  <>
                    <Text color="white">
                      {"Price: "}

                      {packSize && packSizePrice?.price && type === "unit" ? formatPriceWithFOC(packSizePrice.price, 2) : formatPriceWithFOC(currentPrice.price, 2)}
                    </Text>

                    <br />

                    <Text color="white">
                      {"VAT: "}

                      {packSize && packSizePrice?.vat && type === "unit" ? formatPriceWithFOC(packSizePrice.vat, 2) : formatPriceWithFOC(currentPrice.vat, 2)}
                    </Text>
                  </>
                );
              }}
            />
          </>
        )}

        {product && type === "unit" && priceBands.length > 1 && (
          <Grid.Bounds
            className={`price-tier-data ${isCloseToBand() ? "price-tier-close" : ""}`}
            data-tip
            data-for={`price-${product.id}`}
          >
            <>
              <div className="price-tier-wrapper">
                <Icon
                  className="info-icon"
                  icon={Icons.INFO}
                  size={15}
                />

                <Text className="price-tier-text">
                  Volume discounts
                </Text>
              </div>

              <Tooltip
                id={`price-${product.id}`}
                type="dark"
                place="top"
                getContent={() => {
                  return (
                    <PriceTiersToolTip direction="vertical">
                      <Grid.Bounds className="tr th">
                        <Grid.Box className="td">
                          <Text
                            textTransform="uppercase"
                            letterSpacing="big"
                            weight="bold"
                            color="white"
                          >
                            Qty
                          </Text>
                        </Grid.Box>

                        <Grid.Box className="td">
                          <Text
                            textTransform="uppercase"
                            letterSpacing="big"
                            weight="bold"
                            color="white"
                          >
                            Price
                          </Text>
                        </Grid.Box>
                      </Grid.Bounds>

                      {priceBands.map((key, index) => {
                        if (groupedPrices) {
                          const group = groupedPrices[ key ];
                          const nextGroup = groupedPrices[ priceBands[ index + 1 ] ];

                          return (
                            <Grid.Bounds
                              className="tr"
                              key={`price-tiers-${index}`}
                            >
                              <Grid.Box className="td">
                                <Text color="white">
                                  {`${group[ 0 ].minQuantity}${
                                    nextGroup && nextGroup[ 0 ] && nextGroup[ 0 ].minQuantity ? ` - ${nextGroup[ 0 ].minQuantity - 1}` : "+"
                                  } `}
                                </Text>
                              </Grid.Box>

                              <Grid.Box className="dots" />

                              <Grid.Box className="td">
                                <Text color="white">
                                  {getPriceFromBand(group[ 0 ])}
                                </Text>
                              </Grid.Box>
                            </Grid.Bounds>
                          );
                        }

                        return <></>;
                      })}
                    </PriceTiersToolTip>
                  );
                }}
              />
            </>
          </Grid.Bounds>
        )}
      </div>
    </PriceWrapper>
  );
};

const PriceTiersToolTip = styled(Grid.Bounds)`
  ${({ theme }) => css`
    width: 200px;

    .tr {
      justify-content: space-between;
    }

    .dots {
      border-bottom: 2px dotted white;
      margin: 0 ${theme.spacing.padding.xs} 5px;
      flex-grow: 1;
    }
  `}
`;

const PriceWrapper = styled(Grid.Bounds)`
  ${({ theme }) => css`
    align-items: flex-start;

    .old-price {
      position: relative;
      width: auto;
      color: ${theme.colors.lightGrey};

      &:after {
        content: "";
        display: block;
        width: 100%;
        height: 1px;
        background: ${theme.colors.lightGrey};
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
      }
    }

    .vat-wrapper {
      display: inline-block;
      margin-left: ${theme.spacing.margins.xs};

      .inc-vat {
        font-size: 0.9em;
        color: ${theme.colors.midGrey};

        &:hover {
          cursor: help;
        }
      }
    }

    .price-tier-wrapper {
      cursor: help;
      display: flex;
      align-content: center;

      .price-tier-text {
        color: ${theme.colors.lightGrey};
      }

      .info-icon {
        margin-right: ${theme.spacing.padding.xs};
        fill: ${theme.colors.lightGrey};
        cursor: help;
      }
    }

    .price-tier-close {
      .price-tier-text {
        color: ${theme.colors.warn};
      }
      .info-icon {
        fill: ${theme.colors.warn};
      }
    }

    .current-price-wrapper {
      display: flex;
      flex-direction: column;

      .price-tier-data {
        display: flex;
        flex-direction: row;
        align-items: center;

        .price-tier-text {
          .help-icon {
            margin-left: ${theme.spacing.margins.xs};
          }
        }
      }
    }
  `}
`;

export default React.memo(Price);
